/**
 * 需纠偏底图（GCJ-02 等）的 DEM 重采样
 *
 * 全球 terrain-rgb 是 WGS84；显示相机若已是国测局坐标，同一 z/x/y
 * 会采到偏了几百米的海拔。像素经纬度用当前底图适配器 fromDisplayFast
 * 反算回 WGS84 再采样。Mapbox GL 2.15 无 addProtocol，用 fetch 钩子拦自定义地址。
 */

import { getMapboxToken } from '../mapboxToken';
import { getMapCoordAdapter } from './basemapCoord';

export const GCJ_DEM_TILE_PREFIX = 'https://zhxt-dem.invalid/gcj';
export const GCJ_DEM_TILE_TEMPLATE = `${GCJ_DEM_TILE_PREFIX}/{z}/{x}/{y}.png`;

const TILE_SIZE = 256;
const MAX_WGS_CACHE = 48;
const MAX_WARP_CACHE = 64;

const wgsTileCache = new Map();
const warpTileCache = new Map();

let origFetch = null;
let encodeCanvas = null;
let encodeCtx = null;
let decodeCanvas = null;
let decodeCtx = null;

function lngToTileX(lng, z) {
  return ((lng + 180) / 360) * 2 ** z;
}

function latToTileY(lat, z) {
  const s = Math.sin((lat * Math.PI) / 180);
  const clamped = Math.min(Math.max(s, -0.9999), 0.9999);
  return (0.5 - Math.log((1 + clamped) / (1 - clamped)) / (4 * Math.PI)) * 2 ** z;
}

function tileXYToLngLat(x, y, z) {
  const n = 2 ** z;
  const lng = (x / n) * 360 - 180;
  const m = Math.PI - (2 * Math.PI * y) / n;
  const lat = (180 / Math.PI) * Math.atan(Math.sinh(m));
  return { lng, lat };
}

function mapboxRgbToHeight(r, g, b) {
  return -10000 + (r * 256 * 256 + g * 256 + b) * 0.1;
}

function terrariumRgbToHeight(r, g, b) {
  return r * 256 + g + b / 256 - 32768;
}

function heightToMapboxRgb(height) {
  const h = Number.isFinite(height) ? height : 0;
  let v = Math.round((h + 10000) / 0.1);
  if (v < 0) v = 0;
  if (v > 16777215) v = 16777215;
  const r = Math.floor(v / 65536);
  const g = Math.floor((v % 65536) / 256);
  const b = v % 256;
  return [r, g, b];
}

function rememberLru(cache, key, value, max) {
  if (cache.has(key)) cache.delete(key);
  cache.set(key, value);
  while (cache.size > max) {
    const oldest = cache.keys().next().value;
    cache.delete(oldest);
  }
}

function getWgsDemTileUrl(z, x, y) {
  const token = getMapboxToken();
  if (token) {
    return {
      url: `https://api.mapbox.com/v4/mapbox.terrain-rgb/${z}/${x}/${y}.pngraw?access_token=${token}`,
      encoding: 'mapbox'
    };
  }
  return {
    url: `https://s3.amazonaws.com/elevation-tiles-prod/terrarium/${z}/${x}/${y}.png`,
    encoding: 'terrarium'
  };
}

function ensureDecodeCanvas() {
  if (decodeCanvas) return;
  decodeCanvas = document.createElement('canvas');
  decodeCtx = decodeCanvas.getContext('2d', { willReadFrequently: true });
}

function ensureEncodeCanvas() {
  if (encodeCanvas) return;
  encodeCanvas = document.createElement('canvas');
  encodeCanvas.width = TILE_SIZE;
  encodeCanvas.height = TILE_SIZE;
  encodeCtx = encodeCanvas.getContext('2d');
}

async function decodeDemPng(arrayBuffer, encoding) {
  ensureDecodeCanvas();
  const blob = new Blob([arrayBuffer], { type: 'image/png' });
  const bitmap = await createImageBitmap(blob);
  decodeCanvas.width = bitmap.width;
  decodeCanvas.height = bitmap.height;
  decodeCtx.drawImage(bitmap, 0, 0);
  const img = decodeCtx.getImageData(0, 0, bitmap.width, bitmap.height);
  if (typeof bitmap.close === 'function') bitmap.close();
  return {
    width: img.width,
    height: img.height,
    data: img.data,
    encoding
  };
}

async function encodeDemPng(imageData) {
  ensureEncodeCanvas();
  encodeCtx.putImageData(imageData, 0, 0);
  const blob = await new Promise((resolve, reject) => {
    encodeCanvas.toBlob((b) => {
      if (b) resolve(b);
      else reject(new Error('DEM PNG 编码失败'));
    }, 'image/png');
  });
  return blob.arrayBuffer();
}

async function loadWgsTile(z, x, y, fetchFn) {
  const n = 2 ** z;
  const wx = ((x % n) + n) % n;
  if (y < 0 || y >= n) return null;
  const key = `${z}/${wx}/${y}`;
  if (wgsTileCache.has(key)) return wgsTileCache.get(key);

  const pending = (async () => {
    try {
      const spec = getWgsDemTileUrl(z, wx, y);
      const res = await fetchFn(spec.url);
      if (!res.ok) return null;
      const buf = await res.arrayBuffer();
      return decodeDemPng(buf, spec.encoding);
    } catch (err) {
      wgsTileCache.delete(key);
      throw err;
    }
  })();

  rememberLru(wgsTileCache, key, pending, MAX_WGS_CACHE);
  return pending;
}

function heightAtPixel(tile, px, py) {
  if (!tile || px < 0 || py < 0 || px >= tile.width || py >= tile.height) return null;
  const i = (py * tile.width + px) * 4;
  const r = tile.data[i];
  const g = tile.data[i + 1];
  const b = tile.data[i + 2];
  return tile.encoding === 'terrarium'
    ? terrariumRgbToHeight(r, g, b)
    : mapboxRgbToHeight(r, g, b);
}

function pickHeight(values) {
  for (let i = 0; i < values.length; i += 1) {
    if (Number.isFinite(values[i])) return values[i];
  }
  return 0;
}

function sampleWgsHeight(tileMap, z, lng, lat) {
  const n = 2 ** z;
  let xf = lngToTileX(lng, z);
  let yf = latToTileY(lat, z);
  xf = ((xf % n) + n) % n;
  if (yf < 0) yf = 0;
  if (yf >= n) yf = n - 1e-9;

  const tx = Math.floor(xf);
  const ty = Math.floor(yf);
  const tile = tileMap.get(`${z}/${tx}/${ty}`);
  const w = tile?.width || TILE_SIZE;
  const h = tile?.height || TILE_SIZE;
  const sx = (xf - tx) * w - 0.5;
  const sy = (yf - ty) * h - 0.5;
  const x0 = Math.floor(sx);
  const y0 = Math.floor(sy);
  const fx = sx - x0;
  const fy = sy - y0;

  const lookup = (ix, iy) => {
    let tileX = tx;
    let tileY = ty;
    let px = ix;
    let py = iy;
    const tw = w;
    const th = h;
    while (px < 0) {
      px += tw;
      tileX -= 1;
    }
    while (px >= tw) {
      px -= tw;
      tileX += 1;
    }
    while (py < 0) {
      py += th;
      tileY -= 1;
    }
    while (py >= th) {
      py -= th;
      tileY += 1;
    }
    const wrappedX = ((tileX % n) + n) % n;
    return heightAtPixel(tileMap.get(`${z}/${wrappedX}/${tileY}`), px, py);
  };

  const h00 = lookup(x0, y0);
  const h10 = lookup(x0 + 1, y0);
  const h01 = lookup(x0, y0 + 1);
  const h11 = lookup(x0 + 1, y0 + 1);
  const a = pickHeight([h00, h10, h01, h11]);
  const b = pickHeight([h10, h00, h11, h01]);
  const c = pickHeight([h01, h00, h11, h10]);
  const d = pickHeight([h11, h10, h01, h00]);
  return a * (1 - fx) * (1 - fy) + b * fx * (1 - fy) + c * (1 - fx) * fy + d * fx * fy;
}

function displayPixelToWgs(lng, lat) {
  const adapter = getMapCoordAdapter();
  const fn = adapter.fromDisplayFast || adapter.fromDisplay;
  const pair = fn(lng, lat);
  return { lng: pair[0], lat: pair[1] };
}

function gcjPixelToWgs(z, x, y, col, row, size) {
  const { lng, lat } = tileXYToLngLat(x + (col + 0.5) / size, y + (row + 0.5) / size, z);
  return displayPixelToWgs(lng, lat);
}

function listWgsTilesForGcjTile(z, x, y) {
  const needed = new Set();
  const n = 2 ** z;
  const samples = [
    [0, 0],
    [1, 0],
    [0, 1],
    [1, 1],
    [0.5, 0.5],
    [0.5, 0],
    [0.5, 1],
    [0, 0.5],
    [1, 0.5]
  ];
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  samples.forEach(([u, v]) => {
    const wgs = gcjPixelToWgs(z, x, y, u * (TILE_SIZE - 1), v * (TILE_SIZE - 1), TILE_SIZE);
    const tx = lngToTileX(wgs.lng, z);
    const ty = latToTileY(wgs.lat, z);
    minX = Math.min(minX, tx);
    maxX = Math.max(maxX, tx);
    minY = Math.min(minY, ty);
    maxY = Math.max(maxY, ty);
  });
  const x0 = Math.floor(minX) - 1;
  const x1 = Math.floor(maxX) + 1;
  const y0 = Math.max(0, Math.floor(minY) - 1);
  const y1 = Math.min(n - 1, Math.floor(maxY) + 1);
  for (let ty = y0; ty <= y1; ty += 1) {
    for (let tx = x0; tx <= x1; tx += 1) {
      const wx = ((tx % n) + n) % n;
      needed.add(`${z}/${wx}/${ty}`);
    }
  }
  return needed;
}

async function warpGcjDemTile(z, x, y, fetchFn) {
  const cacheKey = `${z}/${x}/${y}`;
  if (warpTileCache.has(cacheKey)) return warpTileCache.get(cacheKey);

  const pending = (async () => {
    try {
      const keys = listWgsTilesForGcjTile(z, x, y);
      const tileMap = new Map();
      await Promise.all(
        [...keys].map(async (key) => {
          const parts = key.split('/');
          const tile = await loadWgsTile(Number(parts[0]), Number(parts[1]), Number(parts[2]), fetchFn);
          if (tile) tileMap.set(key, tile);
        })
      );

      const out = new ImageData(TILE_SIZE, TILE_SIZE);
      const px = out.data;
      for (let row = 0; row < TILE_SIZE; row += 1) {
        for (let col = 0; col < TILE_SIZE; col += 1) {
          const wgs = gcjPixelToWgs(z, x, y, col, row, TILE_SIZE);
          const height = sampleWgsHeight(tileMap, z, wgs.lng, wgs.lat);
          const [r, g, b] = heightToMapboxRgb(height);
          const i = (row * TILE_SIZE + col) * 4;
          px[i] = r;
          px[i + 1] = g;
          px[i + 2] = b;
          px[i + 3] = 255;
        }
      }
      return encodeDemPng(out);
    } catch (err) {
      warpTileCache.delete(cacheKey);
      throw err;
    }
  })();

  rememberLru(warpTileCache, cacheKey, pending, MAX_WARP_CACHE);
  return pending;
}

function parseGcjDemUrl(url) {
  const m = String(url || '').match(/zhxt-dem\.invalid\/gcj\/(\d+)\/(\d+)\/(\d+)/i);
  if (!m) return null;
  return { z: Number(m[1]), x: Number(m[2]), y: Number(m[3]) };
}

function emptyDemPng() {
  ensureEncodeCanvas();
  const [r, g, b] = heightToMapboxRgb(0);
  encodeCtx.fillStyle = `rgb(${r},${g},${b})`;
  encodeCtx.fillRect(0, 0, TILE_SIZE, TILE_SIZE);
  return new Promise((resolve, reject) => {
    encodeCanvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('空 DEM 编码失败'));
        return;
      }
      blob.arrayBuffer().then(resolve, reject);
    }, 'image/png');
  });
}

/**
 * 拦截 Mapbox 对 GCJ DEM 自定义地址的 fetch（2.15 无 addProtocol）。
 */
export function installGcjDemFetchHook() {
  if (typeof window === 'undefined') return;
  if (!window.__zhxtOrigFetch) {
    window.__zhxtOrigFetch = window.fetch.bind(window);
  }
  origFetch = window.__zhxtOrigFetch;
  window.fetch = function zhxtGcjDemFetch(input, init) {
    const url = typeof input === 'string' ? input : input && input.url;
    const parsed = parseGcjDemUrl(url);
    if (!parsed) return origFetch(input, init);
    return warpGcjDemTile(parsed.z, parsed.x, parsed.y, origFetch)
      .then((buf) => new Response(buf, {
        status: 200,
        headers: {
          'Content-Type': 'image/png',
          'Cache-Control': 'public, max-age=86400'
        }
      }))
      .catch(async (err) => {
        console.warn('[terrain3d] GCJ DEM 纠偏失败', parsed, err?.message || err);
        const buf = await emptyDemPng();
        return new Response(buf, {
          status: 200,
          headers: { 'Content-Type': 'image/png' }
        });
      });
  };
  if (!window.__zhxtGcjDemFetchHooked) {
    console.info('[terrain3d] 已安装 GCJ DEM 纠偏 fetch 钩子');
  }
  window.__zhxtGcjDemFetchHooked = true;
}
